import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /**
   * Anpassung (ParadoxEcho): Wiedergabestatus eines Videos, das beim Öffnen des Dialogs
   * in der Galerie lief, damit es im Dialog an derselben Stelle weiterläuft.
   * @type {{ index: number, currentTime: number } | null}
   */
  #handoff = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    // Anpassung: Laufende Galerie-Videos pausieren, bevor der Dialog seine eigene
    // Kopie des Videos anzeigt – sonst läuft der Ton im Hintergrund weiter.
    this.#pauseGalleryMedia();

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }

      this.#resumeInDialog();
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Liefert die Medien-Container der Galerie (außerhalb des Dialogs) in Dialog-Reihenfolge.
   * @returns {HTMLElement[]}
   */
  #galleryMediaContainers() {
    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );
    if (!mediaGallery) return [];

    const slideshowActive = mediaGallery.presentation === 'carousel';
    const containers = slideshowActive ? mediaGallery.slideshow?.slides : mediaGallery.media;

    return Array.from(containers ?? []).filter((element) => element instanceof HTMLElement);
  }

  /**
   * Findet das <deferred-media>-Element eines Containers.
   * @param {HTMLElement | undefined} container
   * @returns {(HTMLElement & { loadContent: (focus?: boolean) => void, playMedia: () => void, pauseMedia: () => void }) | null}
   */
  #deferredMediaIn(container) {
    const element = container?.querySelector('deferred-media');
    return element instanceof HTMLElement && 'pauseMedia' in element ? /** @type {any} */ (element) : null;
  }

  /**
   * Pausiert alle Videos in der Galerie und merkt sich das aktuell laufende Video.
   */
  #pauseGalleryMedia() {
    this.#handoff = null;

    this.#galleryMediaContainers().forEach((container, index) => {
      const deferredMedia = this.#deferredMediaIn(container);
      if (!deferredMedia) return;

      const video = deferredMedia.querySelector('video');
      if (video instanceof HTMLVideoElement && !video.paused && !video.ended) {
        this.#handoff = { index, currentTime: video.currentTime };
      }

      deferredMedia.pauseMedia();
    });
  }

  /**
   * Setzt ein zuvor laufendes Galerie-Video im Dialog an derselben Stelle fort.
   */
  #resumeInDialog() {
    const handoff = this.#handoff;
    this.#handoff = null;
    if (!handoff) return;

    const deferredMedia = this.#deferredMediaIn(this.refs.media[handoff.index]);
    if (!deferredMedia) return;

    deferredMedia.loadContent(false);

    seekVideo(deferredMedia.querySelector('video'), handoff.currentTime);
    deferredMedia.playMedia();
  }

  /**
   * Ermittelt, welches Video im Dialog gerade läuft (muss VOR dem DialogCloseEvent passieren,
   * da dieses alle Medien pausiert).
   * @returns {{ index: number, currentTime: number } | null}
   */
  #captureDialogPlayback() {
    for (const [index, container] of this.refs.media.entries()) {
      const video = this.#deferredMediaIn(container)?.querySelector('video');
      if (video instanceof HTMLVideoElement && !video.paused && !video.ended) {
        return { index, currentTime: video.currentTime };
      }
    }
    return null;
  }

  /**
   * Übergibt ein im Dialog laufendes Video beim Schließen zurück an die Galerie.
   * @param {{ index: number, currentTime: number } | null} playback
   */
  #handBackToGallery(playback) {
    if (!playback) return;

    const galleryMedia = this.#deferredMediaIn(this.#galleryMediaContainers()[playback.index]);
    if (!galleryMedia) return;

    galleryMedia.loadContent(false);

    seekVideo(galleryMedia.querySelector('video'), playback.currentTime);
    galleryMedia.playMedia();
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;

    // Anpassung: Ein im Dialog laufendes Video in der Galerie an derselben Stelle fortsetzen.
    const playback = this.#captureDialogPlayback();

    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());

    this.#handBackToGallery(playback);
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Springt in einem Video an eine Position – auch wenn die Metadaten noch nicht geladen sind
 * (Safari ignoriert currentTime sonst).
 * @param {Element | null} video
 * @param {number} time
 */
function seekVideo(video, time) {
  if (!(video instanceof HTMLVideoElement) || !Number.isFinite(time) || time <= 0) return;

  if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
    video.currentTime = time;
    return;
  }

  video.addEventListener(
    'loadedmetadata',
    () => {
      video.currentTime = time;
    },
    { once: true }
  );
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
